import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
    CheckCircle, ShieldCheck, ArrowRight, Sparkle, Clock, MapPin, EnvelopeSimple,
    User as UserIcon, IdentificationBadge, House, Calendar, Cake, Phone,
    Copy, Download, Warning, Stack, ChartBar, Megaphone, Timer, Lock, CaretDown, SignOut
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { HashChip } from "../components/HashChip";
import ConfirmInfoDialog from "../components/ConfirmInfoDialog";
import EligibilityAffirmationDialog from "../components/EligibilityAffirmationDialog";
import BallotWizard from "../components/BallotWizard";
import ReceiptVerifier from "../components/ReceiptVerifier";

const receiptCheckUrl = (hash) => `${window.location.origin}/login/voter?receipt=${encodeURIComponent(hash || "")}`;
import AccountSettingsDialog from "../components/AccountSettingsDialog";
import { toast } from "sonner";

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
            const [y, m, d] = iso.split("-").map(Number);
            return new Date(y, m - 1, d).toLocaleDateString(undefined, { dateStyle: "short" });
        }
        const d = new Date(iso);
        return d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
    } catch { return iso; }
}

function countdownTo(iso) {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return null;
    const days = Math.floor(ms / 86400000);
    const hrs = Math.floor((ms % 86400000) / 3600000);
    const mins = Math.floor((ms % 3600000) / 60000);
    return `${days}d ${hrs}h ${mins}m`;
}

function windowState(e) {
    const now = Date.now();
    const starts = e.starts_at ? new Date(e.starts_at).getTime() : null;
    const ends = e.ends_at ? new Date(e.ends_at).getTime() : null;
    return {
        before_start: !!(starts && now < starts),
        after_end: !!(ends && now > ends),
        open_now: e.open_now,
    };
}

function InfoRow({ label, value, icon: Icon, mono = false, testid }) {
    return (
        <div data-testid={testid} className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2 min-w-0 gap-0.5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1 shrink-0 sm:w-16">
                {Icon && <Icon size={10} weight="bold" />} {label}
            </div>
            <div className={`text-xs text-slate-900 font-semibold break-words min-w-0 ${mono ? "hash-mono" : ""}`}>
                {value || <span className="text-slate-400 italic font-normal">—</span>}
            </div>
        </div>
    );
}

export default function VoterDashboard() {
    const [profile, setProfile] = useState(null);
    const [elections, setElections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);
    const [confirming, setConfirming] = useState(null); // election awaiting info-confirmation
    const [affirming, setAffirming] = useState(null);   // election awaiting eligibility affirmation
    const [pick, setPick] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [receipt, setReceipt] = useState(null);
    const [sealedInfo, setSealedInfo] = useState(null); // shows "results-sealed" modal for already-voted active election
    const [tick, setTick] = useState(0); // re-render every minute for countdowns
    const [accountOpen, setAccountOpen] = useState(false);
    // Voter Information panel is collapsed by default on mobile so the vote
    // is visible above the fold. On lg+ screens the panel sits in its own
    // column, so we default it open and hide the toggle entirely.
    const [infoOpen, setInfoOpen] = useState(() => {
        if (typeof window === "undefined") return true;
        return window.matchMedia("(min-width: 1024px)").matches;
    });

    useEffect(() => {
        const t = setInterval(() => setTick((x) => x + 1), 60_000);
        return () => clearInterval(t);
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const [p, e] = await Promise.all([api.get("/voter/profile"), api.get("/voter/elections")]);
            setProfile(p.data);
            setElections(e.data);
        } catch { toast.error("Could not load your dashboard"); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const cast = async (payload0) => {
        if (!active) return;
        try {
            const payload = { election_id: active.id };
            const hasCands = (active.candidates || []).length > 0;
            const hasQs = (active.questions || []).length > 0;
            if (hasCands) payload.candidate_id = (payload0 && payload0.candidate_id) || pick || null;
            if (hasQs) payload.answers = (payload0 && payload0.answers) || {};
            if (payload0 && payload0.face_photo) payload.face_photo = payload0.face_photo;
            const { data } = await api.post("/voter/vote", payload);
            setReceipt({ ...data.block, election: active.title });
            setActive(null);
            setPick("");
            await load();
            toast.success("Vote sealed on chain");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Vote failed");
        }
    };

    // Recompute countdowns when minute ticks (use tick as dependency).
    const elsWithTimers = useMemo(() => elections.map((e) => {
        const ws = windowState(e);
        return {
            ...e,
            timer: countdownTo(e.ends_at),
            timer_to_start: ws.before_start ? countdownTo(e.starts_at) : null,
            ws,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [elections, tick]);

    const { logout } = useAuth();
    const nav = useNavigate();
    const nothingPending = !elsWithTimers.some((e) => e.ws.open_now && !e.already_voted && !e.is_over);
    const signOutNow = async () => {
        setReceipt(null);
        await logout();
        toast.dismiss();
        toast.success("Thanks for voting — you've been signed out");
        nav("/login/voter");
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
                <div className="grid lg:grid-cols-12 gap-4">
                    {/* Left: voter info */}
                    <aside className="lg:col-span-4 space-y-4 lg:order-1 order-2" data-testid="voter-info-panel">
                        <Card className="relative overflow-hidden p-4 bg-gradient-to-b from-white to-slate-50/70 border-slate-200/80 shadow-md hover:shadow-lg transition-shadow ring-1 ring-slate-200/60">
                            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-civic-500 via-civic-400 to-emerald-400" aria-hidden="true" />
                            <div className={`flex items-center gap-2 ${infoOpen ? "pb-2 border-b border-slate-200/70" : ""}`}>
                                <button
                                    type="button"
                                    onClick={() => setInfoOpen((v) => !v)}
                                    className="lg:hidden -m-1 p-1 flex items-center gap-2 flex-1 min-w-0 text-left"
                                    aria-expanded={infoOpen}
                                    aria-controls="voter-info-body"
                                    data-testid="voter-info-toggle"
                                >
                                    <div className="h-7 w-7 grid place-items-center rounded-md bg-civic-50 border border-civic-100 shadow-inner shrink-0">
                                        <UserIcon size={14} weight="duotone" className="text-civic-700" />
                                    </div>
                                    <h2 className="font-heading font-bold text-sm text-slate-900 tracking-tight flex-1">Voter Information</h2>
                                    <CaretDown
                                        size={14}
                                        weight="bold"
                                        className={`text-slate-500 shrink-0 transition-transform ${infoOpen ? "rotate-180" : ""}`}
                                    />
                                </button>
                                <div className="hidden lg:flex items-center gap-2 flex-1">
                                    <div className="h-7 w-7 grid place-items-center rounded-md bg-civic-50 border border-civic-100 shadow-inner">
                                        <UserIcon size={14} weight="duotone" className="text-civic-700" />
                                    </div>
                                    <h2 className="font-heading font-bold text-sm text-slate-900 tracking-tight">Voter Information</h2>
                                </div>
                                <div className="ml-auto shrink-0">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setAccountOpen(true)}
                                        className="h-7 px-2.5 text-[11px] hash-mono border-civic-200 hover:bg-civic-50 text-civic-700"
                                        data-testid="manage-account-btn"
                                    >
                                        <Lock size={11} weight="duotone" className="mr-1" /> Manage
                                    </Button>
                                </div>
                            </div>
                            <div
                                id="voter-info-body"
                                className={`mt-3 space-y-1.5 ${infoOpen ? "block" : "hidden lg:block"}`}
                                data-testid="voter-info-body"
                            >
                                <InfoRow label="Name" icon={UserIcon} value={profile?.name} testid="info-name" />
                                <InfoRow label="Voter ID" icon={IdentificationBadge} value={profile?.voter_id} mono testid="info-voter-id" />
                                <InfoRow label="Precinct" icon={MapPin} value={profile?.precinct} mono testid="info-precinct" />
                                <InfoRow
                                    label="Born / Age"
                                    icon={Cake}
                                    value={(() => {
                                        const dobStr = profile?.dob ? new Date(profile.dob + "T00:00:00").toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null;
                                        const ageStr = profile?.age != null ? `${profile.age} yrs` : null;
                                        if (dobStr && ageStr) return `${dobStr} · ${ageStr}`;
                                        return dobStr || ageStr || null;
                                    })()}
                                    testid="info-age"
                                />
                                <InfoRow label="Email" icon={EnvelopeSimple} value={profile?.email} testid="info-email" />
                                <InfoRow label="Mobile" icon={Phone} value={profile?.mobile} testid="info-mobile" />
                                <InfoRow label="Address" icon={House} value={profile?.address} testid="info-address" />
                                <InfoRow label="Last Vote" icon={Calendar} value={profile?.last_voted_at ? fmtDate(profile.last_voted_at) : "No votes yet"} testid="info-last-vote" />
                            </div>
                        </Card>
                        <ReceiptVerifier compact />
                    </aside>

                    {/* Right: elections selector — active-only on mobile so Recently Closed can drop to the bottom */}
                    <main className="lg:col-span-8 lg:order-2 order-1" data-testid="elections-panel">
                        {loading ? (
                            <div className="hash-mono text-slate-500 text-sm">loading…</div>
                        ) : (
                            <>
                                {/* Mobile: active only. Desktop: both active + closed together. */}
                                <div className="lg:hidden">
                                    <ElectionGroups items={elsWithTimers} only="active" onVote={(e) => { setConfirming(e); setPick(""); }} onSealed={(e) => setSealedInfo(e)} />
                                </div>
                                <div className="hidden lg:block">
                                    <ElectionGroups items={elsWithTimers} onVote={(e) => { setConfirming(e); setPick(""); }} onSealed={(e) => setSealedInfo(e)} />
                                </div>
                            </>
                        )}
                    </main>

                    {/* Mobile-only: Recently Closed drops to the bottom below Voter Info + Verify */}
                    {!loading && (
                        <section className="lg:hidden order-3" data-testid="closed-elections-mobile-bottom">
                            <ElectionGroups items={elsWithTimers} only="closed" collapsibleClosed onVote={(e) => { setConfirming(e); setPick(""); }} onSealed={(e) => setSealedInfo(e)} />
                        </section>
                    )}
                </div>
            </div>

            {/* Confirm-info gate shown before the ballot */}
            <ConfirmInfoDialog
                open={!!confirming}
                profile={profile}
                election={confirming}
                onExit={() => setConfirming(null)}
                onProfileChange={(p) => setProfile(p)}
                onConfirm={() => { setAffirming(confirming); setConfirming(null); }}
            />

            {/* Account settings modal — change PIN / email / mobile / address with challenge */}
            <AccountSettingsDialog
                open={accountOpen}
                onOpenChange={setAccountOpen}
                profile={profile}
                onSaved={load}
            />

            {/* Eligibility affirmation gate (shown after info confirm, before ballot) */}
            <EligibilityAffirmationDialog
                open={!!affirming}
                election={affirming}
                address={profile?.address}
                onExit={() => setAffirming(null)}
                onProceed={() => { setActive(affirming); setAffirming(null); }}
            />

            {/* Ballot wizard — handles candidates AND/OR ballot questions */}
            <BallotWizard
                open={!!active && (((active?.candidates || []).length > 0) || ((active?.questions || []).length > 0))}
                election={active}
                onExit={() => { setActive(null); setPick(""); }}
                onSubmit={(payload) => cast(payload)}
            />

            {/* Sealed-results modal — shown when voter clicks an already-voted active election */}
            <SealedResultsDialog
                open={!!sealedInfo}
                election={sealedInfo}
                onClose={() => setSealedInfo(null)}
            />

            {/* Receipt dialog — shown only ONCE after a vote is sealed */}
            <Dialog open={!!receipt} onOpenChange={() => { /* prevent dismiss-on-outside-click */ }}>
                <DialogContent className={`${nothingPending ? "sm:max-w-xl" : "sm:max-w-lg"} max-h-[92vh] overflow-y-auto`} data-testid="receipt-dialog" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
                    <DialogHeader className="space-y-1">
                        <div className="flex items-center gap-2">
                            <CheckCircle size={26} weight="fill" className="text-emerald-600" />
                            <DialogTitle className="font-heading text-xl">Vote sealed on chain</DialogTitle>
                        </div>
                        <DialogDescription className="text-xs">
                            Recorded anonymously in the public ledger for <span className="font-semibold">{receipt?.election}</span>.
                        </DialogDescription>
                    </DialogHeader>

                    {/* One-time warning */}
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2" data-testid="receipt-warning">
                        <Warning size={16} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
                        <div className="text-xs text-amber-900 leading-snug">
                            <span className="font-semibold">This receipt is shown only once.</span> Write down or copy the <span className="font-semibold">full hash</span> below — you'll need it to verify your vote later on the Explorer page.
                        </div>
                    </div>

                    <div className="space-y-2.5 bg-slate-50 border border-slate-200 rounded-md p-3">
                        <div className="flex items-center justify-between">
                            <span className="overline text-slate-500">Block #</span>
                            <span className="hash-mono text-sm font-semibold text-slate-900" data-testid="receipt-index">{receipt?.index}</span>
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <span className="overline text-slate-500">Block hash · SHA-256</span>
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try {
                                            await navigator.clipboard.writeText(receipt?.hash || "");
                                            toast.success("Full hash copied to clipboard");
                                        } catch {
                                            toast.error("Could not copy. Long-press the hash to copy manually.");
                                        }
                                    }}
                                    className="text-[10px] text-civic-700 hover:text-civic-900 font-semibold flex items-center gap-1"
                                    data-testid="receipt-copy-hash"
                                >
                                    <Copy size={11} weight="bold" /> Copy
                                </button>
                            </div>
                            <div
                                className="hash-mono text-[11px] sm:text-xs text-slate-900 break-all bg-white border border-slate-200 rounded p-2 select-all"
                                data-testid="receipt-hash-full"
                            >
                                {receipt?.hash}
                            </div>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="overline text-slate-500">Date</span>
                            <span className="hash-mono text-[11px] text-slate-700">{receipt?.timestamp?.slice(0, 10)}</span>
                        </div>
                    </div>

                    <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                const text = `E-VoteVerify.com Vote Receipt\n\nElection : ${receipt?.election}\nBlock #  : ${receipt?.index}\nHash     : ${receipt?.hash}\nDate     : ${receipt?.timestamp?.slice(0, 10)}\n\nCheck online : ${receiptCheckUrl(receipt?.hash)}\nOr paste the hash into "Verify Your Receipt" on the sign-in page.\nThis receipt is anonymous — it does not link to your identity.\n`;
                                const blob = new Blob([text], { type: "text/plain" });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `evoteverify-receipt-block-${receipt?.index}.txt`;
                                document.body.appendChild(a); a.click(); a.remove();
                                URL.revokeObjectURL(url);
                            }}
                            className="text-xs"
                            data-testid="receipt-download-btn"
                        >
                            <Download size={14} className="mr-1.5" weight="bold" /> Download .txt
                        </Button>
                        <Button
                            onClick={() => setReceipt(null)}
                            className="bg-civic-600 hover:bg-civic-700 text-white text-xs"
                            data-testid="receipt-close-btn"
                        >
                            <CheckCircle size={14} className="mr-1.5" weight="fill" /> I have saved my hash
                        </Button>
                        {nothingPending && (
                            <Button
                                onClick={signOutNow}
                                className="bg-slate-900 hover:bg-slate-800 text-white text-xs"
                                data-testid="receipt-signout-btn"
                            >
                                <SignOut size={14} className="mr-1.5" weight="bold" /> Sign out now
                            </Button>
                        )}
                    </DialogFooter>
                    {nothingPending && (
                        <p className="text-[11px] text-slate-500 text-center sm:text-right" data-testid="receipt-all-done-note">
                            You have no other open ballots. Save your hash, then sign out.
                        </p>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function ElectionGroups({ items, onVote, onSealed, only = "both", collapsibleClosed = false }) {
    const active = items.filter((e) => !e.is_over);
    const closed = items.filter((e) => e.is_over);
    // Closed section starts collapsed when explicitly requested (mobile).
    const [closedOpen, setClosedOpen] = useState(!collapsibleClosed);
    if (items.length === 0) {
        return (
            <Card className="p-6 text-center border-slate-200 bg-white" data-testid="no-elections">
                <Sparkle size={24} weight="duotone" className="text-civic-600 mx-auto mb-2" />
                <div className="font-heading font-bold text-base text-slate-900">Nothing on your ballot today</div>
                <p className="text-xs text-slate-500 mt-1">Check back soon — new elections will appear here as they open in your precinct.</p>
            </Card>
        );
    }
    const showActive = only !== "closed";
    const showClosed = only !== "active";
    if (only === "closed" && closed.length === 0) return null;
    if (only === "active" && active.length === 0) return null;
    return (
        <div className="space-y-3">
            {showActive && active.length > 0 && (
                <section data-testid="active-elections-section">
                    <SectionTitle icon={Megaphone} label={`Active Elections (${active.length})`} tone="open" />
                    <div className="mt-1.5 space-y-1.5" data-testid="elections-list">
                        {active.map((e) => <ElectionCard key={e.id} e={e} onVote={onVote} onSealed={onSealed} />)}
                    </div>
                </section>
            )}
            {showClosed && closed.length > 0 && (
                <section data-testid="closed-elections-section">
                    {collapsibleClosed ? (
                        <button
                            type="button"
                            onClick={() => setClosedOpen((v) => !v)}
                            className="w-full flex items-center gap-1.5 -m-1 p-1 text-left"
                            aria-expanded={closedOpen}
                            aria-controls="closed-elections-body"
                            data-testid="closed-elections-toggle"
                        >
                            <div className="h-5 w-5 grid place-items-center rounded bg-slate-100 border border-slate-200 shrink-0">
                                <ChartBar size={11} weight="duotone" className="text-slate-600" />
                            </div>
                            <h2 className="font-heading font-bold text-xs text-slate-900 flex-1">{`Recently Closed (${closed.length})`}</h2>
                            <CaretDown
                                size={12}
                                weight="bold"
                                className={`text-slate-500 shrink-0 transition-transform ${closedOpen ? "rotate-180" : ""}`}
                            />
                        </button>
                    ) : (
                        <SectionTitle icon={ChartBar} label={`Recently Closed (${closed.length})`} tone="closed" />
                    )}
                    {(!collapsibleClosed || closedOpen) && (
                        <div
                            id="closed-elections-body"
                            className="mt-1.5 space-y-1.5"
                            data-testid="closed-elections-list"
                        >
                            {closed.map((e) => <ElectionCard key={e.id} e={e} onVote={onVote} onSealed={onSealed} />)}
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}

function SectionTitle({ icon: Icon, label, tone }) {
    return (
        <div className="flex items-center gap-1.5">
            <div className={`h-5 w-5 grid place-items-center rounded ${
                tone === "open" ? "bg-emerald-50 border border-emerald-200" : "bg-slate-100 border border-slate-200"
            }`}>
                <Icon size={11} weight="duotone" className={tone === "open" ? "text-emerald-700" : "text-slate-600"} />
            </div>
            <h2 className="font-heading font-bold text-xs text-slate-900">{label}</h2>
        </div>
    );
}

function ElectionCard({ e, onVote, onSealed }) {
    const canVote = e.ws.open_now && !e.already_voted;
    const subtitle = [e.authority, e.precincts && e.precincts.length > 0 ? `Pct: ${e.precincts.join(", ")}` : null]
        .filter(Boolean).join(" • ");

    // Whole-card click behaviour
    let clickable = false;
    let onCardClick = null;
    if (e.is_over) {
        clickable = true;
        onCardClick = () => { window.location.href = `/results/${e.id}`; };
    } else if (canVote) {
        clickable = true;
        onCardClick = () => onVote(e);
    } else if (e.already_voted) {
        clickable = true;
        onCardClick = () => onSealed && onSealed(e);
    }

    let statusEl;
    if (e.is_over) {
        statusEl = (
            <span className="inline-flex items-center gap-1 text-slate-700 font-semibold text-[11px] hash-mono">
                <Timer size={11} weight="fill" /> Closed
            </span>
        );
    } else if (e.ws?.before_start) {
        statusEl = (
            <span
                className="inline-flex items-center gap-1 text-amber-700 font-semibold text-[11px] hash-mono"
                title={e.starts_at ? `Polls open ${new Date(e.starts_at).toLocaleString()}` : "Opens soon"}
            >
                <Timer size={11} weight="fill" /> Opens in {e.timer_to_start || "soon"}
            </span>
        );
    } else if (e.timer) {
        statusEl = (
            <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-[11px] hash-mono">
                <Timer size={11} weight="fill" /> {e.timer}
            </span>
        );
    } else {
        statusEl = (
            <span className="inline-flex items-center gap-1 text-amber-700 font-semibold text-[11px] hash-mono">
                <Timer size={11} weight="fill" /> Opens soon
            </span>
        );
    }

    const itemsCount = e.items_count ?? ((e.candidates || []).length + (e.questions || []).length);

    return (
        <Card
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? onCardClick : undefined}
            onKeyDown={clickable ? (ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); onCardClick(); } } : undefined}
            className={`relative overflow-hidden px-3 py-2.5 bg-gradient-to-b from-white to-slate-50/70 border-slate-200/80 ring-1 ring-slate-200/60 shadow-md transition-all flex flex-col gap-2 ${clickable ? "cursor-pointer hover:shadow-xl hover:-translate-y-0.5 hover:ring-civic-300/50" : "hover:shadow-lg"}`}
            data-testid={`election-card-${e.id}`}
        >
            {/* Status accent strip */}
            <div
                className={`absolute inset-x-0 top-0 h-1 ${e.is_over
                    ? "bg-gradient-to-r from-slate-400 to-slate-300"
                    : canVote
                        ? "bg-gradient-to-r from-emerald-500 via-emerald-400 to-civic-400"
                        : e.already_voted
                            ? "bg-gradient-to-r from-civic-500 via-civic-400 to-emerald-400"
                            : "bg-gradient-to-r from-amber-400 to-amber-300"
                }`}
                aria-hidden="true"
            />
            {/* Top row — meta */}
            <div className="flex items-start gap-2.5">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                        <h3 className="font-heading font-bold text-sm text-slate-900 leading-tight break-words">{e.title}</h3>
                        {e.already_voted && !e.is_over && (
                            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[9px] hash-mono px-1 py-0 h-4 shrink-0" data-testid={`voted-${e.id}`}>
                                <CheckCircle size={9} weight="fill" className="mr-0.5" /> Voted
                            </Badge>
                        )}
                    </div>
                    {subtitle && (
                        <div className="mt-0.5 text-[11px] text-slate-600 truncate leading-tight">{subtitle}</div>
                    )}
                </div>
                <div className="shrink-0 flex items-center gap-1 px-1.5 py-0.5 rounded bg-civic-50 border border-civic-100 text-civic-700" data-testid={`items-tile-${e.id}`}>
                    <Stack size={10} weight="bold" />
                    <span className="font-heading font-bold text-xs leading-none">{itemsCount}</span>
                </div>
            </div>

            {/* Bottom row — status (left) + action button (right). Always present, same shape on every card. */}
            <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-slate-100">
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    {statusEl}
                    {e.is_over && e.participation_pct !== null && e.participation_pct !== undefined && (
                        <>
                            <span className="text-slate-300">·</span>
                            <span className="inline-flex items-center gap-1 text-civic-700 font-semibold text-[11px] hash-mono">
                                <ChartBar size={11} weight="fill" /> {e.participation_pct}%
                            </span>
                        </>
                    )}
                </div>
                <div className="shrink-0">
                    {canVote && (
                        <Button
                            onClick={(ev) => { ev.stopPropagation(); onVote(e); }}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-md px-2.5 h-7 text-[11px]"
                            data-testid={`vote-now-${e.id}`}
                        >
                            <CheckCircle size={11} className="mr-1" weight="fill" /> Vote
                            <ArrowRight size={10} className="ml-1" weight="bold" />
                        </Button>
                    )}
                    {e.is_over && (
                        <a href={`/results/${e.id}`} className="inline-flex" onClick={(ev) => ev.stopPropagation()}>
                            <Button variant="outline" className="h-7 text-[11px] px-2.5" data-testid={`view-results-${e.id}`}>
                                <ChartBar size={11} className="mr-1" weight="bold" /> View results
                            </Button>
                        </a>
                    )}
                    {!e.is_over && e.already_voted && (
                        <Button
                            variant="outline"
                            onClick={(ev) => { ev.stopPropagation(); onSealed && onSealed(e); }}
                            className="h-7 text-[11px] px-2.5 border-civic-200 text-civic-700 hover:bg-civic-50"
                            data-testid={`view-sealed-${e.id}`}
                        >
                            <ShieldCheck size={11} className="mr-1" weight="bold" /> Sealed
                        </Button>
                    )}
                </div>
            </div>
        </Card>
    );
}

function SealedResultsDialog({ open, election, onClose }) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (!open) return;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [open]);

    if (!election) return null;

    const ends = election.ends_at ? new Date(election.ends_at).getTime() : null;
    const ms = ends ? Math.max(0, ends - now) : 0;
    const days = Math.floor(ms / 86400000);
    const hrs = Math.floor((ms % 86400000) / 3600000);
    const mins = Math.floor((ms % 3600000) / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    const closesStr = ends ? new Date(ends).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

    const TimeUnit = ({ value, label }) => (
        <div className="flex flex-col items-center min-w-[58px]">
            <div className="hash-mono text-2xl font-black text-civic-700 leading-none tabular-nums">
                {String(value).padStart(2, "0")}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{label}</div>
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="sm:max-w-md" data-testid="sealed-results-dialog">
                <DialogHeader className="space-y-1">
                    <div className="flex items-center gap-2">
                        <ShieldCheck size={22} weight="fill" className="text-civic-600" />
                        <DialogTitle className="font-heading text-lg">Results sealed</DialogTitle>
                    </div>
                    <DialogDescription className="text-xs">
                        Your vote in <span className="font-semibold text-slate-800">{election.title}</span> is recorded on the chain. Tallies stay hidden until polls close to protect election integrity.
                    </DialogDescription>
                </DialogHeader>

                <div className="rounded-lg border border-civic-100 bg-civic-50/60 px-3 py-3" data-testid="sealed-countdown">
                    <div className="text-[10px] uppercase tracking-wider text-civic-700 font-bold flex items-center gap-1.5">
                        <Timer size={11} weight="fill" /> Results post in
                    </div>
                    <div className="mt-2 flex items-end justify-between gap-1.5">
                        <TimeUnit value={days} label="Days" />
                        <span className="text-2xl font-black text-civic-300 leading-none mb-4">:</span>
                        <TimeUnit value={hrs} label="Hours" />
                        <span className="text-2xl font-black text-civic-300 leading-none mb-4">:</span>
                        <TimeUnit value={mins} label="Mins" />
                        <span className="text-2xl font-black text-civic-300 leading-none mb-4">:</span>
                        <TimeUnit value={secs} label="Secs" />
                    </div>
                    <div className="mt-3 pt-2 border-t border-civic-100 flex items-center justify-between text-[11px]">
                        <span className="text-slate-600">Polls close</span>
                        <span className="hash-mono font-semibold text-slate-900" data-testid="sealed-ends-at">{closesStr}</span>
                    </div>
                </div>

                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 flex items-start gap-2">
                    <CheckCircle size={14} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                    <div className="text-[11px] text-emerald-900 leading-snug">
                        Your ballot has been counted. Once results post, you can verify your vote on the Explorer page using your block hash.
                    </div>
                </div>

                <DialogFooter>
                    <Button onClick={onClose} className="bg-civic-600 hover:bg-civic-700 text-white text-xs h-8" data-testid="sealed-close-btn">
                        Got it
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
